import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, type RequestDto } from "../lib/api";
import { useQueue } from "../hooks/useQueue";
import type { ChannelContext } from "../components/Layout";
import { NowPlaying } from "../components/NowPlaying";
import { QueueSection } from "../components/QueueSection";
import { AddPanel } from "../components/AddPanel";
import { QueueSkeleton } from "../components/Skeleton";

export function Queue() {
  const { channelId } = useOutletContext<ChannelContext>();
  const { data, error, loading, live, refresh } = useQueue(channelId);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [opError, setOpError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  async function op(id: string | null, body: Record<string, unknown>) {
    setBusyId(id);
    setOpError(null);
    try {
      // Every queue op is scoped to the active channel (authz + fan-out).
      await api.queueOp({ channelId, ...body });
      await refresh(); // WS will also nudge us; refreshing keeps it snappy
    } catch (err) {
      setOpError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  const complete = (r: RequestDto) => op(r.id, { op: "complete", requestId: r.id });
  const skip = (r: RequestDto) => op(r.id, { op: "skip", requestId: r.id });
  const remove = (r: RequestDto) => op(r.id, { op: "remove", requestId: r.id });
  const move = (r: RequestDto, position: number) =>
    op(r.id, { op: "move", requestId: r.id, position });
  const togglePin = (r: RequestDto) =>
    op(r.id, { op: "pin", requestId: r.id, pinned: !r.manuallyPositioned });
  const editAmount = (r: RequestDto, amountCents: number) =>
    op(r.id, { op: "setMatched", requestId: r.id, amountCents });
  const editNotes = (r: RequestDto, notes: string) =>
    op(r.id, { op: "setNotes", requestId: r.id, notes });
  const editLink = (r: RequestDto, youtubeUrl: string) =>
    op(r.id, { op: "setLink", requestId: r.id, youtubeUrl });
  const reactivate = (r: RequestDto) =>
    op(r.id, { op: "reactivate", requestId: r.id });

  async function playback(action: "play" | "pause") {
    try {
      await api.overlayControl(channelId, action);
    } catch (err) {
      setOpError(err instanceof Error ? err.message : String(err));
    }
  }

  // Skipped requests share the completed section server-side (status makes the
  // distinction), so split them apart for display.
  const completed = data?.completed.filter((r) => r.status !== "skipped") ?? [];
  const skipped = data?.completed.filter((r) => r.status === "skipped") ?? [];

  // No channel (e.g. a mod with no grants) — the Layout banner explains.
  if (!channelId) return null;

  return (
    <>
      <div className="tab-head">
        <span className={`live ${live ? "on" : ""}`}>
          <span className="dot" />
          {live ? "live" : "reconnecting…"}
        </span>
      </div>

      {error && <div className="error">Couldn’t load the queue — {error}</div>}
      {opError && <div className="error">{opError}</div>}

      {loading && !data ? (
        <QueueSkeleton />
      ) : data ? (
        <>
          <NowPlaying
            request={data.nowPlaying}
            busy={busyId === data.nowPlaying?.id}
            onComplete={() => data.nowPlaying && complete(data.nowPlaying)}
            onSkip={() => data.nowPlaying && skip(data.nowPlaying)}
            onPlayback={data.settings.nowPlayingPlaybackEnabled ? playback : undefined}
          />

          <QueueSection
            title="Paid"
            section="paid"
            items={data.paid}
            emptyText="No paid requests yet."
            busyId={busyId}
            onComplete={complete}
            onSkip={skip}
            onRemove={remove}
            onTogglePin={togglePin}
            onEditAmount={editAmount}
            onEditNotes={editNotes}
            onEditLink={editLink}
            onMove={move}
          />

          <QueueSection
            title="Free"
            section="free"
            items={data.free}
            emptyText="No free requests yet."
            busyId={busyId}
            onComplete={complete}
            onSkip={skip}
            onRemove={remove}
            onTogglePin={togglePin}
            onEditAmount={editAmount}
            onEditNotes={editNotes}
            onEditLink={editLink}
            onMove={move}
          />

          <QueueSection
            title="Completed"
            section="completed"
            items={completed}
            emptyText="Nothing played yet."
            busyId={busyId}
            onReactivate={reactivate}
            note={data.completedTruncated ? "most recent only" : undefined}
            collapsible
          />

          <QueueSection
            title="Skipped"
            section="skipped"
            items={skipped}
            emptyText="Nothing skipped yet."
            busyId={busyId}
            onReactivate={reactivate}
            note={data.completedTruncated ? "most recent only" : undefined}
            collapsible
          />

          <button
            type="button"
            className="fab"
            onClick={() => setAddOpen(true)}
            aria-label="Add to queue"
            title="Add to queue"
          >
            +
          </button>

          {addOpen && (
            <AddPanel
              channelId={channelId}
              onChanged={refresh}
              onClose={() => setAddOpen(false)}
            />
          )}
        </>
      ) : null}
    </>
  );
}
